import React from "react";
import { PageHeader, KpiCard, KpiGrid, Panel, Table, useAdminQuery, RefreshButton, Unavailable, INK, MUTE, INDIGO } from "../_shared";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from "recharts";

export default function AdminAI() {
  const stats  = useAdminQuery("/kalki/admin/validator-stats", { days: 30 });
  const weekly = useAdminQuery("/kalki/admin/validator-stats/weekly", { weeks: 12 });
  const top    = useAdminQuery("/kalki/admin/validator-stats/top", { days: 30, top_n: 10 });
  const metrics = useAdminQuery("/admin/metrics");

  if (stats.error && stats.error?.response?.status === 403)
    return <Unavailable error={stats.error} />;

  const s = stats.data || {};
  const m = metrics.data || {};
  const total_msgs = m?.conversations?.messages_total ?? "—";
  const failed = m?.conversations?.failed_replies ?? "—";
  const total_7d  = m?.conversations?.messages_7d ?? "—";

  return (
    <div data-testid="admin-ai-page">
      <PageHeader title="AI Activity"
        subtitle="Chat & Voice telemetry over the last 30 days."
        right={<RefreshButton loading={stats.loading} onClick={() => { stats.refresh(); weekly.refresh(); top.refresh(); metrics.refresh(); }} />} />

      <KpiGrid>
        <KpiCard label="Messages total" value={total_msgs} testId="kpi-messages-total" />
        <KpiCard label="Messages · 7d"  value={total_7d}   testId="kpi-messages-7d" />
        <KpiCard label="Failed replies" value={failed}     testId="kpi-failed-replies" />
        <KpiCard label="Validator runs" value={s.total}    hint="30 days"           testId="kpi-validator-runs" />
        <KpiCard label="Regenerated"    value={s.regenerated} hint={`${pct(s.regenerated, s.total)}%`} testId="kpi-regenerated" />
        <KpiCard label="Dropped"        value={s.dropped}  hint={`${pct(s.dropped, s.total)}%`}     testId="kpi-dropped" />
      </KpiGrid>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        <Panel title="Validator volume · weekly" testId="panel-validator-weekly">
          {weekly.loading ? <Loading /> : weekly.error ? <Unavailable error={weekly.error} /> : (
            <WeeklyTrend data={weekly.data?.weeks} />
          )}
        </Panel>
        <Panel title="Top rule hits · 30d" testId="panel-top-rules">
          {top.loading ? <Loading /> : top.error ? <Unavailable error={top.error} /> : (
            <Table cols={[
              { label: "Rule",  render: (x) => <code style={{ fontSize: 11 }}>{x.rule}</code> },
              { label: "Hits",  render: (x) => (x.count || 0).toLocaleString() },
            ]} rows={top.data?.top || []} />
          )}
        </Panel>
      </div>
    </div>
  );
}

function WeeklyTrend({ data }) {
  const rows = (data || []).map((d) => ({ w: d.week?.slice(5) || "", v: d.total || 0 }));
  if (!rows.length) return <div style={{ color: MUTE, fontSize: 12 }}>Not instrumented.</div>;
  return (
    <ResponsiveContainer width="100%" height={160}>
      <AreaChart data={rows}>
        <defs>
          <linearGradient id="g-v" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor={INDIGO} stopOpacity="0.3" />
            <stop offset="1" stopColor={INDIGO} stopOpacity="0" />
          </linearGradient>
        </defs>
        <XAxis dataKey="w" tick={{ fontSize: 9, fill: MUTE }} />
        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E8E6EF" }} />
        <Area type="monotone" dataKey="v" stroke={INDIGO} strokeWidth={2} fill="url(#g-v)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function pct(a, b) { if (!b) return 0; return Math.round(100 * (a || 0) / b); }
function Loading() { return <div style={{ padding: 30, color: MUTE, fontSize: 13, textAlign: "center" }}>Loading…</div>; }
